<script lang="ts">
	//Formatters
	import { format } from 'svelte-i18n';
	//Design system components
	import {
		Radio,
		Button,
		Modal,
		Container,
		Text,
		Loader,
		Alert,
		Stack,
	} from '@Shelly/shelly-design-system';
	//Helpers
	import {
		addAddonPeripheral,
		getPeripheralFormat,
		getPeripheralsTypes,
		scanPeripheralByType,
	} from 'src/helpers/addons/addon_scan_helpers';
	import { isProSensorAddon } from 'src/helpers/addons/addon_support_helpers';
	//Components
	import IOPeripheralScan from './IOPeripheralScan.svelte';
	import OneWirePeripheralScan from './OneWirePeripheralScan.svelte';
	//Types
	import type { Device } from 'src/types/Device';
	import type { SettingRequest } from 'src/types/requests/settings/setting';
	import type { SensorScanComponent, SensorScanResult, SensorType } from 'src/types/Peripherals';

	export let device: Device,
		showModal = false;

	const peripheralsTypes = getPeripheralsTypes(device);
	const availablePeripherals: SensorType[] = Object.keys(peripheralsTypes) as SensorType[];
	const proSensorAddon = isProSensorAddon(device);

	const addPeripheral = async () => {
		waitForResponse = true;
		const res: SettingRequest = await addAddonPeripheral(
			device,
			selectedSensorType,
			selectedSensorComponent,
		);
		if (res?.isok) {
			showModal = false;
		}
		waitForResponse = false;
	};

	const onSelectPeripheral = async (peripheralType: SensorType) => {
		waitGetPeripheralsRes = true;
		selectedSensorComponent = null;
		peripheralScanResult = await scanPeripheralByType(device, peripheralType);
		waitGetPeripheralsRes = false;
	};

	/**
	 * Checks if the peripheral has preselected peripheral component
	 * or the user can select which component to use
	 * @param selectedSensorType
	 * @param selectedSensorComponent
	 */
	const hasPreselectedComponent = (
		selectedSensorType: SensorType,
		selectedSensorComponent: SensorScanComponent,
	): boolean => {
		if (selectedSensorComponent !== null) {
			return true;
		}
		if (proSensorAddon) {
			return selectedSensorType === 'digital_out';
		}
		return selectedSensorType !== 'ds18b20';
	};

	let selectedSensorType: SensorType = availablePeripherals[0];
	let selectedSensorComponent: SensorScanComponent | null = null;
	let peripheralScanResult: SensorScanResult;
	let waitGetPeripheralsRes = false;
	let waitForResponse = false;

	$: if (selectedSensorType) {
		onSelectPeripheral(selectedSensorType);
	}

	$: isSaveDisabled =
		waitForResponse ||
		waitGetPeripheralsRes ||
		peripheralScanResult?.disabled ||
		!hasPreselectedComponent(selectedSensorType, selectedSensorComponent);
</script>

<Modal
	{showModal}
	on:close={() => (showModal = false)}
	title={$format('title_add_addon_peripheral', {
		default: 'Add Add-on Peripheral',
	})}
>
	<div slot="content">
		<Container padding="0">
			<Stack
				class="mb-m"
				gap="s"
				direction="column"
				directionMobile="row"
				align="between"
				alignMobile="none"
			>
				<Text styleType="h2" class="width-full align-self-center">
					{$format('text_title_select_addon_peripheral', {
						default: 'Select add-on peripheral',
					})}
				</Text>
				<Button
					icon="fa-solid fa-arrows-rotate"
					size="m"
					class="width-md-auto width-sm-full"
					disabled={waitGetPeripheralsRes}
					on:click={() => onSelectPeripheral(selectedSensorType)}
				>
					{$format('button_rescan', { default: 'Rescan' })}
				</Button>
			</Stack>

			{#if waitGetPeripheralsRes}
				<Loader size="l" class=" mb-s d-flex justify-content-center" />
			{/if}
			<Stack gap="s">
				{#each availablePeripherals as peripheral, i}
					<Radio
						id="peripheral-{peripheral}"
						bind:selected={selectedSensorType}
						value={peripheral}
						disabled={waitGetPeripheralsRes}
					>
						{getPeripheralFormat(peripheralsTypes[peripheral])}
					</Radio>
					{#if !waitGetPeripheralsRes && peripheral === selectedSensorType}
						{#if peripheralScanResult?.error}
							<Alert type="warning" heading={peripheralScanResult.error} />
						{:else if peripheralScanResult?.sensorType === 'ds18b20'}
							<OneWirePeripheralScan {peripheralScanResult} bind:selectedSensorComponent />
						{:else if proSensorAddon && peripheralScanResult?.sensorType !== 'digital_out'}
							<IOPeripheralScan
								{selectedSensorType}
								{peripheralScanResult}
								bind:selectedSensorComponent
							/>
						{/if}
					{/if}
				{/each}
			</Stack>
		</Container>
	</div>

	<div class="d-flex justify-content-end" slot="footer">
		<Button
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s text-capitalized"
			responsiveSize={{
				sm: 'm',
			}}
			variant="secondary"
			on:click={() => {
				showModal = false;
			}}
		>
			{$format('button_cancel', { default: 'Cancel' })}
		</Button>
		<Button
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
			responsiveSize={{
				sm: 'm',
			}}
			loading={waitForResponse}
			disabled={isSaveDisabled}
			on:click={addPeripheral}
		>
			{$format('button_add_peripheral', { default: 'Add peripheral' })}
		</Button>
	</div>
</Modal>
